<!-- layouts/default.vue -->
<template>
  <!--
    Avoid Quasar's SSR layout components here. After the Quasar/Nuxt upgrade,
    QLayout/QPage read $q.screen while server-rendering and can crash before
    the client hydrates. The drawer/CTA pieces remain client-only, while the
    page shell can be rendered safely with native elements.
  -->
  <div class="fast-app-shell tw-flex tw-flex-col">
    <LandingPageLoading custom-loading-pic-size="tw-w-36 tw-h-14" />
    <ThemedComponent
      path="Layout"
      component-name="Header"
      :is-homepage="isHomepage"
      @click-mobile-menu="toggleDrawer"
    />
    <ClientOnly>
      <div
        class="fast-mobile-drawer-layout"
        style="position: fixed; inset: 0; pointer-events: none; z-index: 5000;"
      >
        <QLayout container view="hHh lpR fFf">
          <NavigationMobileDrawer v-model="showDrawer" />
        </QLayout>
      </div>
      <template #fallback><div class="q-drawer-container" /></template>
    </ClientOnly>
    <ThemedComponent
      path="Layout"
      component-name="ContainerPage"
      :is-homepage="isHomepage"
      class="tw-flex-1 tw-flex tw-flex-col"
    >
      <main
        data-allow-mismatch
        :oncontextmenu="isDisableClick"
        :class="['tw-flex-1', isDisableSelect]"
      >
        <slot />
        <ThemedComponent
          v-if="!isProductCTA"
          path="Layout"
          component-name="CTABottomBar.client"
        />
      </main>
    </ThemedComponent>
    <ThemedComponent path="Layout" component-name="Footer" />
  </div>
</template>

<script lang="ts" setup>
import { MENU_CODE } from '~/constants/menu';

const { domain, slugPageInfo } = useNavigationMenu();
// Force https:// in schema @id values — the SSR server receives http:// internally
// (Cloudflare terminates TLS), but all public-facing Schema.org entity IDs must be https.
const httpsOrigin = computed(() => domain.value.replace(/^http:\/\//, 'https://'));
const langStore = useLanguageStore();
const { currentLocaleCode } = useLanguage();
const contactStore = useContactStore();
const websiteInfoStore = useWebsiteInfoStore();
const isDisableClick = computed(() =>
  websiteInfoStore.copyDisabled ? 'return false;' : 'return true;',
);
const isDisableSelect = computed(() =>
  websiteInfoStore.copyDisabled ? 'tw-select-none' : '',
);

const showDrawer = ref(false);
const toggleDrawer = () => {
  showDrawer.value = !showDrawer.value;
};

const syncFixedViewportInsets = () => {
  if (!import.meta.client) {
    return;
  }

  const root = document.documentElement;
  const viewportTop = window.visualViewport?.offsetTop || 0;
  const viewportHeight = window.visualViewport?.height || window.innerHeight;
  const viewportBottom = Math.max(0, window.innerHeight - viewportTop - viewportHeight);

  root.style.setProperty('--fast-fixed-top', `${Math.round(viewportTop)}px`);
  root.style.setProperty('--fast-fixed-bottom', `${Math.round(viewportBottom)}px`);
};

const homepageList = ['index', 'preview-banner'];
const route = useRoute();
const getRouteBaseName = useRouteBaseName();
const isHomepage = computed(() =>
  homepageList.includes(getRouteBaseName(route) || ''),
);

const statusStore = useStatusStore();
watch(route, (newRoute, oldRoute) => {
  const isPreloading =
    !!import.meta.env.CLIENT && newRoute.name !== oldRoute.name;
  statusStore.updatePreloading(isPreloading);

  const isChangePath = newRoute.fullPath !== oldRoute.fullPath;
  statusStore.updateIsChangePath(isChangePath);
});

onMounted(() => {
  syncFixedViewportInsets();

  window.visualViewport?.addEventListener('resize', syncFixedViewportInsets, { passive: true });
  window.addEventListener('resize', syncFixedViewportInsets, { passive: true });
  window.addEventListener('orientationchange', syncFixedViewportInsets, { passive: true });
});

onUnmounted(() => {
  window.visualViewport?.removeEventListener('resize', syncFixedViewportInsets);
  window.removeEventListener('resize', syncFixedViewportInsets);
  window.removeEventListener('orientationchange', syncFixedViewportInsets);
});

const isProductCTA = computed(
  () =>
    (slugPageInfo.value?.code === MENU_CODE.PRODUCT ||
      slugPageInfo.value?.code === MENU_CODE.PROMOTION) &&
    !!route.params.slugDetail,
);

const contactPoints = contactStore.phoneNumbers?.length
  ? contactStore.phoneNumbers
      .filter(Boolean) // filters out empty strings/null
      .map((number) => ({
        '@type': 'ContactPoint',
        telephone: number,
        contactType: 'Contact',
      }))
  : undefined;
const socialLinks = [contactStore.youtubeLink, contactStore.tiktokLink].filter(
  Boolean,
);

const workTranslation = langStore.value
  .filter((lang) => !lang.default)
  .map((lang) => ({
    '@id': `${httpsOrigin.value}/${lang.code}#website`,
  }));

useSchemaOrg([
  defineImage({
    '@id': `${httpsOrigin.value}/#logo`,
    url: websiteInfoStore.logo?.large || '',
    caption: websiteInfoStore.name,
    inLanguage: currentLocaleCode.value,
  }),
  defineLocalBusiness({
    '@id': `${httpsOrigin.value}/#organization`,
    name: websiteInfoStore.name,
    description: websiteInfoStore.description,
    url: httpsOrigin.value,
    logo: {
      '@id': `${httpsOrigin.value}/#logo`,
    },
    image: {
      '@id': `${httpsOrigin.value}/#logo`,
    },
    address: {
      postalCode: contactStore.zipcode,
      streetAddress: contactStore.address,
      addressLocality: contactStore.province,
    },
    ...(contactStore.phoneNumbers.length
      ? { telephone: contactStore.phoneNumbers[0] }
      : {}),
    ...(contactPoints ? { contactPoint: contactPoints } : {}),
    ...(socialLinks.length ? { sameAs: socialLinks } : {}),
  }),
  defineWebSite({
    '@id': `${httpsOrigin.value}/#website`,
    name: websiteInfoStore.name,
    description: websiteInfoStore.description,
    url: httpsOrigin.value,
    inLanguage: currentLocaleCode.value,
    publisher: {
      '@id': `${httpsOrigin.value}/#organization`,
    },
    workTranslation,
  }),
  defineWebPage({
    '@id': `${httpsOrigin.value}/#webpage`,
    name: websiteInfoStore.name,
    description: websiteInfoStore.description,
    url: httpsOrigin.value,
    potentialAction: [],
    about: {
      '@id': `${httpsOrigin.value}/#organization`,
    },
    isPartOf: {
      '@id': `${httpsOrigin.value}/#website`,
    },
    primaryImageOfPage: {
      '@id': `${httpsOrigin.value}/#logo`,
    },
  }),
]);
</script>

<style>
.fast-app-shell {
  min-height: 100svh;
  width: 100%;
  overflow-x: clip;
}

.fast-mobile-drawer-layout .q-layout {
  pointer-events: none !important;
}
.fast-mobile-drawer-layout .q-drawer,
.fast-mobile-drawer-layout .q-drawer__backdrop:not(.hidden) {
  pointer-events: auto !important;
}
</style>
